import type { ExpressionToken, ParsedToken } from '../../../types/QueryBuilder';

/** Replace a token's span in the raw query string via character-offset splicing. */
export function replaceToken(query: string, token: ParsedToken, replacement: string): string {
  return query.substring(0, token.location.start.offset) + replacement + query.substring(token.location.end.offset);
}

/** Remove only the enclosing parentheses of an expression group, keeping the inner content. */
export function unwrapExpression(query: string, expression: ExpressionToken): string {
  // Skip the optional `<expressionType>(` prefix and the trailing `)` so a typed
  // group like `anySpan2(span_name:=:foo)` unwraps to `span_name:=:foo` instead of
  // chopping just the first and last character of the prefix.
  const openOffset = expression.location.start.offset + (expression.expressionType?.length ?? 0) + 1;
  const closeOffset = expression.location.end.offset - 1;
  const inner = query.substring(openOffset, closeOffset).trim();
  return replaceToken(query, expression, inner).replace(/\s+/g, ' ').trim();
}

/** Remove a token from the raw query string and normalize whitespace. */
export function removeToken(query: string, token: ParsedToken): string {
  const before = query.substring(0, token.location.start.offset);
  const after = query.substring(token.location.end.offset);
  return (before + after).replace(/\s+/g, ' ').trim();
}

/**
 * Wrap a field name or value in double-quotes if it contains characters that are
 * structural delimiters in the query grammar (whitespace, colon, or comma).
 * An empty value is always serialized as "" (the grammar's representation of empty).
 */
export function quoteIfNeeded(text: string): string {
  // Fix: a `"` typed inside a value can't be stored — it would break the query — so drop any
  // quotes first, then only wrap the value in quotes if it has a space, colon, or comma.
  const stripped = text.replaceAll('"', '');
  if (stripped === '') {
    return '""';
  }
  if (/[\s:,]/.test(stripped)) {
    return `"${stripped}"`;
  }
  return stripped;
}

/**
 * Serialize a parsed token back to its raw string form.
 * Used when mutating semantic fields (e.g. changing operator) rather than splicing .text directly.
 */
export function stringifyToken(token: ParsedToken): string {
  switch (token.type) {
    case 'FILTER': {
      const prefix = token.negated ? '!' : '';
      const field = quoteIfNeeded(token.field);
      // An empty array joins to '', producing `field:op:` (no value) which FILTER_RE
      // rejects and re-parses as FREE_TEXT; emit `""` so the FILTER round-trips.
      const valueStr = Array.isArray(token.value)
        ? token.value.length > 0
          ? token.value.map((v) => quoteIfNeeded(String(v))).join(',')
          : '""'
        : quoteIfNeeded(String(token.value ?? ''));
      return `${prefix}${field}:${token.operator}:${valueStr}`;
    }
    case 'FREE_TEXT':
      return token.value.includes(' ') ? `"${token.value}"` : token.value;
    case 'LOGIC_BOOL':
      return token.value;
    case 'EXPRESSION':
      return `${token.expressionType ?? ''}(${token.inner.map(stringifyToken).join(' ')})`;
    default:
      throw new Error(`Unsupported token type: ${(token as ParsedToken).type}`);
  }
}
